import axios from 'axios';
import apiClient from '../api/apiClient';
import { getRecommendationConnection } from './meetingRecommendationConfig';

// Temporary demo fallback requested for integration. Explicit IDs always win.
// Replace/disable this once bookings persist the selected venue ID.
export const TEMPORARY_LOCATION_ID = process.env.REACT_APP_SNAPSOFT_DEMO_LOCATION_ID || '338';
const proxyPath = process.env.REACT_APP_MEETING_LOCATIONS_PATH?.trim();
const direct = getRecommendationConnection({
  directEnabled: process.env.REACT_APP_SNAPSOFT_DIRECT_ENABLED,
  endpoint: process.env.REACT_APP_SNAPSOFT_ENDPOINT,
  apiKey: process.env.REACT_APP_SNAPSOFT_API_KEY,
});
const basePath = proxyPath || direct?.url.replace(/\/recommendations$/, '/locations');
const client = axios.create({ withCredentials: false });

export function resolveLocationId(locationId) {
  return positiveId(locationId ?? TEMPORARY_LOCATION_ID, 'Location ID');
}
function positiveId(value, label) {
  if (!/^[1-9]\d*$/.test(String(value)) || !Number.isSafeInteger(Number(value))) {
    throw new Error(`${label} must be a positive integer.`);
  }
  return Number(value);
}
function ratingValue(rating) {
  if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
    throw new Error('Choose a whole-number rating from 1 to 5 paws.');
  }
  return rating;
}
function requiredText(value, label) {
  if (typeof value !== 'string' || !value.trim()) throw new Error(`${label} is required.`);
  return value.trim();
}
async function request(method, suffix, body, signal) {
  if (!basePath) throw new Error('Venue API is not configured.');
  const config = { signal, timeout: 20000 };
  const transport = proxyPath ? apiClient : client;
  if (!proxyPath) config.headers = { 'Content-Type': 'application/json', 'x-api-key': direct.apiKey };
  const url = basePath.replace(/\/+$/, '') + suffix;
  const response = method === 'delete'
    ? await transport.delete(url, config)
    : await transport[method](url, body, config);
  return response.data;
}

/** Creates a venue or returns an existing venue resolved by place_id. */
export async function createMeetingLocation(details, { signal } = {}) {
  const payload = {
    name: requiredText(details?.name, 'Venue name'),
    neighborhood: requiredText(details?.neighborhood, 'Neighborhood'),
    location_type: requiredText(details?.location_type, 'Location type'),
    address: requiredText(details?.address, 'Address'),
  };
  // Only send real external rating data supplied by the caller, never demo values.
  if (details.external_rating != null) {
    if (typeof details.external_rating !== 'number' || !Number.isFinite(details.external_rating) || details.external_rating < 0 || details.external_rating > 5) throw new Error('External rating must be between 0 and 5.');
    payload.external_rating = details.external_rating;
  }
  if (details.external_review_count != null) {
    if (!Number.isSafeInteger(details.external_review_count) || details.external_review_count < 0) throw new Error('External review count must be a non-negative integer.');
    payload.external_review_count = details.external_review_count;
  }
  const data = await request('post', '', payload, signal);
  if (!data?.location?.location_id) throw new Error('The venue service did not return a location ID.');
  return data;
}

/** POST is an upsert in the supplied contract; userId is the signed-in user's ID. */
export async function createLocationRating({ locationId, userId, rating }, { signal } = {}) {
  return request('post', `/${resolveLocationId(locationId)}/ratings/${positiveId(userId, 'User ID')}`, { rating: ratingValue(rating) }, signal);
}

export async function updateLocationRating({ locationId, userId, rating }, { signal } = {}) {
  return request('put', `/${resolveLocationId(locationId)}/ratings/${positiveId(userId, 'User ID')}`, { rating: ratingValue(rating) }, signal);
}

/** Soft-deletes a shared venue. Only call from an explicit management action. */
export async function deactivateMeetingLocation(locationId, { signal } = {}) {
  const id = resolveLocationId(locationId);
  const data = await request('delete', `/${id}`, undefined, signal);
  if (!['deactivated', 'already_inactive'].includes(data?.status)) throw new Error('The venue service did not confirm deactivation.');
  return data;
}
